import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAccessibility } from "../context/AccessibilityContext";
import { LogOut, User, Menu, X, ChevronDown, Type } from "lucide-react";
import { useState } from "react";

export default function Navbar() {
    const { user, logout } = useAuth();
    const { textSize, setTextSize } = useAccessibility();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [userMenu, setUserMenu] = useState(false);

    const handleLogout = async () => { await logout(); navigate("/"); };
    const role = user?.role;
    const isAdmin = role === "super_admin" || role === "btc_admin";
    const isJury = role === "jury";

    const link = (to, label, testId) => (
        <NavLink
            to={to}
            data-testid={testId}
            end={to === "/"}
            className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-semibold transition-colors ${
                    isActive ? "text-[var(--navy)]" : "text-slate-700 hover:text-[var(--navy)]"
                }`
            }
            onClick={() => setOpen(false)}
        >
            {({ isActive }) => (
                <>
                    {label}
                    {isActive && <span className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 w-7 h-0.5 bg-[var(--gold)] rounded-full" />}
                </>
            )}
        </NavLink>
    );

    return (
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-[var(--border)] shadow-sm">
            <div className="container-x flex items-center justify-between h-16">
                <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
                    <img src="/logo-cucdanso.png" alt="Cục Dân số" className="w-11 h-11 rounded-full object-contain shadow-sm" />
                    <div className="leading-tight">
                        <div className="font-heading font-extrabold text-[var(--navy)] text-sm">CỤC DÂN SỐ</div>
                        <div className="text-[10px] text-[var(--gold-deep)] font-bold tracking-widest">BỘ Y TẾ · VIỆT NAM</div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {link("/", "Trang chủ", "nav-home")}
                    {link("/cuoc-thi", "Cuộc thi", "nav-contests")}
                    {link("/thu-vien", "Thư viện", "nav-library")}
                    {link("/bang-xep-hang", "Xếp hạng", "nav-leaderboard")}
                    {link("/binh-chon", "Bình chọn", "nav-voting")}
                    {link("/thong-ke", "Thống kê", "nav-analytics")}
                    {isJury && link("/giam-khao", "BGK", "nav-jury")}
                    {isAdmin && link("/quan-tri", "Quản trị", "nav-admin")}
                </nav>

                <div className="hidden lg:flex items-center gap-2">
                    {/* Text size toggle */}
                    <div className="flex items-center gap-1 border border-[var(--border)] rounded-lg p-0.5">
                        <button onClick={() => setTextSize("normal")}
                            className={`px-2 py-1 rounded text-xs font-bold flex items-center gap-1 ${
                                textSize === "normal" ? "bg-[var(--navy)] text-white" : "text-slate-600 hover:bg-[var(--bg-alt)]"
                            }`}
                            title="Cỡ chữ thường" data-testid="text-size-normal">
                            <Type className="w-3 h-3" /> A
                        </button>
                        <button onClick={() => setTextSize("large")}
                            className={`px-2 py-1 rounded text-sm font-bold flex items-center gap-1 ${
                                textSize === "large" ? "bg-[var(--navy)] text-white" : "text-slate-600 hover:bg-[var(--bg-alt)]"
                            }`}
                            title="Cỡ chữ lớn (cho người cao tuổi)" data-testid="text-size-large">
                            <Type className="w-4 h-4" /> A+
                        </button>
                    </div>

                    {user ? (
                        <div className="relative">
                            <button onClick={() => setUserMenu(!userMenu)}
                                className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-[var(--border)] hover:border-[var(--navy)] transition"
                                data-testid="user-menu-btn">
                                {user.avatar_url
                                    ? <img src={user.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                                    : <div className="w-7 h-7 rounded-full bg-[var(--gold)]/20 flex items-center justify-center text-[var(--gold-deep)] text-xs font-bold">{user.full_name?.[0]}</div>}
                                <span className="text-sm font-medium text-[var(--navy)] max-w-[140px] truncate">{user.full_name}</span>
                                <ChevronDown className="w-3 h-3 text-slate-400" />
                            </button>
                            {userMenu && (
                                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-[var(--border)] shadow-lg py-2" onMouseLeave={() => setUserMenu(false)}>
                                    <div className="px-4 py-2 border-b border-[var(--border)]">
                                        <div className="text-xs text-slate-500">{roleLabel(role)}</div>
                                        <div className="font-mono text-xs text-[var(--gold-deep)] mt-0.5">{user.candidate_code || user.email}</div>
                                    </div>
                                    <Link to="/ho-so" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-[var(--bg-alt)]" data-testid="profile-link">
                                        <User className="w-4 h-4" /> Hồ sơ
                                    </Link>
                                    <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-[var(--bg-alt)] w-full text-left text-[var(--red)]" data-testid="logout-btn">
                                        <LogOut className="w-4 h-4" /> Đăng xuất
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            <Link to="/dang-nhap" className="btn-ghost" data-testid="login-link">Đăng nhập</Link>
                            <Link to="/dang-ky" className="btn-gold !py-2 !px-4 text-sm" data-testid="register-link">Đăng ký</Link>
                        </>
                    )}
                </div>

                <button className="lg:hidden text-[var(--navy)]" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
                    {open ? <X /> : <Menu />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-[var(--border)] bg-white">
                    <div className="container-x py-4 flex flex-col gap-1">
                        {link("/", "Trang chủ", "mnav-home")}
                        {link("/cuoc-thi", "Cuộc thi", "mnav-contests")}
                        {link("/thu-vien", "Thư viện", "mnav-library")}
                        {link("/bang-xep-hang", "Xếp hạng", "mnav-leaderboard")}
                        {link("/binh-chon", "Bình chọn", "mnav-voting")}
                        {link("/thong-ke", "Thống kê", "mnav-analytics")}
                        {isJury && link("/giam-khao", "BGK", "mnav-jury")}
                        {isAdmin && link("/quan-tri", "Quản trị", "mnav-admin")}
                        <div className="flex items-center gap-2 px-4 py-2 border-t border-[var(--border)] mt-2">
                            <span className="text-xs text-slate-500">Cỡ chữ:</span>
                            <button onClick={() => setTextSize("normal")} className={`px-2 py-1 rounded text-xs ${textSize === "normal" ? "bg-[var(--navy)] text-white" : "border border-[var(--border)]"}`}>A</button>
                            <button onClick={() => setTextSize("large")} className={`px-2 py-1 rounded text-sm ${textSize === "large" ? "bg-[var(--navy)] text-white" : "border border-[var(--border)]"}`}>A+</button>
                        </div>
                        <div className="divider my-2" />
                        {user ? (
                            <>
                                <Link to="/ho-so" className="px-4 py-2 text-sm">Hồ sơ — {user.full_name}</Link>
                                <button onClick={handleLogout} className="text-left text-[var(--red)] px-4 py-2 text-sm">Đăng xuất</button>
                            </>
                        ) : (
                            <>
                                <Link to="/dang-nhap" className="btn-secondary text-center">Đăng nhập</Link>
                                <Link to="/dang-ky" className="btn-gold text-center">Đăng ký</Link>
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}

function roleLabel(r) {
    return { super_admin: "Quản trị tối cao", btc_admin: "Ban Tổ chức", jury: "Ban Giám khảo", participant: "Thí sinh", voter: "Người vote" }[r] || r;
}
